import React, { useEffect, useState, useCallback } from 'react';
import '../super-admin.css';
import { 
  Typography, Card, Row, Col, Statistic, Table, Tag, Button, Select, 
  DatePicker, message, Space, Tooltip, Modal, Form
} from 'antd';
import { 
  DollarOutlined, RiseOutlined, UserOutlined, CreditCardOutlined,
  BarChartOutlined, FileTextOutlined, PlusOutlined,
  ArrowUpOutlined, ArrowDownOutlined, CalendarOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';
// Simple chart components will be implemented inline

const { Title } = Typography;
const { RangePicker } = DatePicker;

const RevenueEarnings = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [monthlyChartData, setMonthlyChartData] = useState([]);
  const [statements, setStatements] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [plansStats, setPlansStats] = useState({});
  const [statementType, setStatementType] = useState('monthly');
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [form] = Form.useForm();


  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/revenue/dashboard`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load dashboard data');
      const data = await res.json();
      
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      message.error('Failed to load dashboard data');
    }
  }, []);

  // Load monthly chart data
  const loadMonthlyChart = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/revenue/monthly-chart`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load chart data');
      const data = await res.json();
      
      // Format data for chart
      const formattedData = data.map(item => ({
        month: new Date(item.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue: parseFloat(item.revenue) || 0,
        transactions: parseInt(item.transactions) || 0,
        customers: parseInt(item.unique_customers) || 0
      }));
      
      setMonthlyChartData(formattedData);
    } catch (err) {
      console.error('Error loading chart data:', err);
    }
  }, []);

  // Load subscription plans data
  const loadSubscriptionPlans = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/revenue/subscription-plans`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load subscription plans');
      const data = await res.json();
      
      setSubscriptionPlans(data.plans || []);
      setPlansStats(data.statistics || {});
    } catch (err) {
      console.error('Error loading subscription plans:', err);
      message.error('Failed to load subscription plans');
    }
  }, []);

  // Load financial statements
  const loadStatements = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/revenue/statements?type=${statementType}&limit=12`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load statements');
      const data = await res.json();
      
      setStatements(data);
    } catch (err) {
      console.error('Error loading statements:', err);
    }
  }, [statementType]);

  // Generate financial statement
  const generateStatement = async (values) => {
    try {
      const formattedValues = {
        statement_type: values.statement_type,
        period_start: values.period_dates[0].format('YYYY-MM-DD'),
        period_end: values.period_dates[1].format('YYYY-MM-DD')
      };

      const res = await fetch(`${apiBase}/admin/revenue/generate-statement`, {
        method: 'POST',
        headers: { 
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formattedValues)
      });
      
      if (!res.ok) throw new Error('Failed to generate statement');
      
      message.success('Financial statement generated successfully');
      setGenerateModalVisible(false);
      form.resetFields();
      loadStatements();
    } catch (err) {
      message.error(err.message || 'Error generating statement');
    }
  };

  useEffect(() => {
    loadDashboardData();
    loadMonthlyChart();
    loadSubscriptionPlans();
    loadStatements();
  }, [loadDashboardData, loadMonthlyChart, loadSubscriptionPlans, loadStatements]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Simple revenue trend component
  const RevenueChart = () => {
    const maxRevenue = Math.max(...monthlyChartData.map(d => d.revenue), 1);
    
    return (
      <div style={{ padding: '20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'end', height: 200, gap: 8 }}>
          {monthlyChartData.map((item, index) => {
            const height = (item.revenue / maxRevenue) * 180;
            return (
              <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Tooltip title={`${item.month}: ${formatCurrency(item.revenue)}`}>
                  <div
                    style={{
                      height: height || 5,
                      backgroundColor: '#059669',
                      width: '100%',
                      borderRadius: '4px 4px 0 0',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </Tooltip>
                <div style={{ fontSize: 10, marginTop: 4, transform: 'rotate(-45deg)', transformOrigin: 'center' }}>
                  {item.month}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Revenue by plan component
  const PlanRevenueChart = () => {
    const planData = dashboardData.revenue_by_plan?.map(plan => ({
      plan: plan.plan_name,
      revenue: parseFloat(plan.total_revenue) || 0,
      subscribers: parseInt(plan.subscribers) || 0
    })) || [];

    const totalRevenue = planData.reduce((sum, plan) => sum + plan.revenue, 0);
    const colors = ['#059669', '#3730a3', '#d97706'];

    return (
      <div style={{ padding: 20 }}>
        {planData.map((plan, index) => {
          const percentage = totalRevenue > 0 ? (plan.revenue / totalRevenue) * 100 : 0;
          return (
            <div key={plan.plan} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 500 }}>{plan.plan}</span>
                <span>{formatCurrency(plan.revenue)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ 
                  flex: 1, 
                  height: 8, 
                  backgroundColor: '#f0f0f0', 
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${percentage}%`, 
                    height: '100%', 
                    backgroundColor: colors[index % colors.length],
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <span style={{ fontSize: 12, color: '#64748b', minWidth: 40 }}>
                  {percentage.toFixed(1)}%
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {plan.subscribers} subscribers
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Subscription plans table columns
  const plansColumns = [
    {
      title: 'Plan Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => name || record.title || `Plan ${record.id}`
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type, record) => type || record.plan_type || 'Standard'
    },
    {
      title: 'Monthly Price',
      dataIndex: 'monthly_price',
      key: 'monthly_price',
      render: (price, record) => formatCurrency(price || record.month_price || record.price || 0)
    },
    {
      title: 'Yearly Price',
      dataIndex: 'yearly_price',
      key: 'yearly_price',
      render: (price, record) => formatCurrency(price || record.year_price || 0)
    },
    {
      title: 'Active Subscribers',
      dataIndex: 'active_subscribers',
      key: 'active_subscribers',
      render: (count) => parseInt(count) || 0
    },
    {
      title: 'Total Revenue',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      render: (revenue) => formatCurrency(revenue)
    },
    {
      title: 'Avg Revenue/User',
      dataIndex: 'avg_revenue_per_user',
      key: 'avg_revenue_per_user',
      render: (avg) => formatCurrency(avg)
    }
  ];

  // Transaction columns
  const transactionColumns = [
    {
      title: 'Date',
      dataIndex: 'processed_at',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString(),
      width: 100
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.user_name}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{record.user_email}</div>
        </div>
      ),
      width: 200
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'type',
      render: (type) => {
        const colors = {
          subscription: 'green',
          upgrade: 'blue',
          refund: 'red',
          chargeback: 'red'
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
      width: 100
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan',
      width: 120
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <span style={{ fontWeight: 500, color: '#059669' }}>
          {formatCurrency(amount)}
        </span>
      ),
      width: 100
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          completed: 'green',
          pending: 'orange',
          failed: 'red',
          refunded: 'red'
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
      width: 100
    }
  ];

  // Statement columns
  const statementColumns = [
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {new Date(record.period_start).toLocaleDateString()} - {new Date(record.period_end).toLocaleDateString()}
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{record.statement_type}</div>
        </div>
      )
    },
    {
      title: 'Total Revenue',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      render: (amount) => (
        <span style={{ fontWeight: 500, color: '#059669' }}>
          {formatCurrency(amount)}
        </span>
      )
    },
    {
      title: 'Net Revenue',
      dataIndex: 'net_revenue',
      key: 'net_revenue',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Subscriptions',
      key: 'subscriptions',
      render: (_, record) => (
        <div>
          <div>Active: {record.active_subscriptions}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            New: {record.new_subscriptions} | Cancelled: {record.cancelled_subscriptions}
          </div>
        </div>
      )
    },
    {
      title: 'MRR',
      dataIndex: 'mrr',
      key: 'mrr',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Churn Rate',
      dataIndex: 'churn_rate',
      key: 'churn_rate',
      render: (rate) => `${parseFloat(rate || 0).toFixed(2)}%`
    }
  ];

  return (
    <div className="sa-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>Revenue & Earnings Dashboard</Title>
        
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Monthly Revenue"
              value={dashboardData.current_month?.revenue || 0}
              formatter={(value) => formatCurrency(value)}
              prefix={<DollarOutlined style={{ color: '#059669' }} />}
              suffix={
                dashboardData.current_month?.growth_rate !== undefined && (
                  <span style={{ 
                    fontSize: 12, 
                    color: dashboardData.current_month.growth_rate >= 0 ? '#059669' : '#dc2626' 
                  }}>
                    {dashboardData.current_month.growth_rate >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {Math.abs(dashboardData.current_month.growth_rate).toFixed(1)}%
                  </span>
                )
              }
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Monthly Recurring Revenue"
              value={dashboardData.mrr || 0}
              formatter={(value) => formatCurrency(value)}
              prefix={<RiseOutlined style={{ color: '#3730a3' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Active Subscriptions"
              value={dashboardData.subscription_stats?.active || 0}
              prefix={<UserOutlined style={{ color: '#d97706' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Average Revenue Per User"
              value={dashboardData.subscription_stats?.arpu || 0}
              formatter={(value) => formatCurrency(value)}
              prefix={<CreditCardOutlined style={{ color: '#7c3aed' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChartOutlined />
                <span>Revenue Trend (12 Months)</span>
              </div>
            }
            hoverable 
            style={{ borderRadius: 14 }}
          >
            <RevenueChart />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChartOutlined />
                <span>Revenue by Plan</span>
              </div>
            }
            hoverable 
            style={{ borderRadius: 14 }}
          >
            <PlanRevenueChart />
          </Card>
        </Col>
      </Row>

      {/* Recent Transactions */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CreditCardOutlined />
            <span>Recent Transactions</span>
          </div>
        }
        hoverable 
        style={{ borderRadius: 14, marginBottom: 24 }}
      >
        <Table
          columns={transactionColumns}
          dataSource={dashboardData.recent_transactions || []}
          rowKey="id"
          pagination={false}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Subscription Plans */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CreditCardOutlined />
            <span>Subscription Plans</span>
          </div>
        }
        hoverable 
        style={{ borderRadius: 14, marginBottom: 24 }}
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} lg={8}>
            <Statistic
              title="Total Plans"
              value={plansStats.total_plans || 0}
              prefix={<CreditCardOutlined style={{ color: '#059669' }} />}
            />
          </Col>
          <Col xs={12} sm={8} lg={8}>
            <Statistic
              title="Active Plans"
              value={plansStats.active_plans || 0}
              prefix={<CheckCircleOutlined style={{ color: '#d97706' }} />}
            />
          </Col>
          <Col xs={12} sm={8} lg={8}>
            <Statistic
              title="Total Subscribers"
              value={plansStats.total_subscribers || 0}
              prefix={<UserOutlined style={{ color: '#7c3aed' }} />}
            />
          </Col>
        </Row>
        
        <Table
          columns={plansColumns}
          dataSource={subscriptionPlans}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Financial Statements */}
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileTextOutlined />
              <span>Financial Statements</span>
            </div>
            <Space>
              <Select
                value={statementType}
                onChange={setStatementType}
                style={{ width: 120 }}
              >
                <Select.Option value="monthly">Monthly</Select.Option>
                <Select.Option value="quarterly">Quarterly</Select.Option>
                <Select.Option value="yearly">Yearly</Select.Option>
              </Select>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setGenerateModalVisible(true)}
              >
                Generate Statement
              </Button>
            </Space>
          </div>
        }
        hoverable 
        style={{ borderRadius: 14 }}
      >
        <Table
          columns={statementColumns}
          dataSource={statements}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Generate Statement Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarOutlined />
            <span>Generate Financial Statement</span>
          </div>
        }
        open={generateModalVisible}
        onCancel={() => {
          setGenerateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={generateStatement}
        >
          <Form.Item
            name="statement_type"
            label="Statement Type"
            rules={[{ required: true, message: 'Please select statement type' }]}
          >
            <Select placeholder="Select type">
              <Select.Option value="monthly">Monthly</Select.Option>
              <Select.Option value="quarterly">Quarterly</Select.Option>
              <Select.Option value="yearly">Yearly</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="period_dates"
            label="Period"
            rules={[{ required: true, message: 'Please select period dates' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setGenerateModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Generate Statement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RevenueEarnings;
